import { deriveFlavourFromAppId } from "@monorepo/shared/analytics";

import { color } from "../theme/designTokens";

export { deriveFlavourFromAppId, isRelevantAppId, isRelevantFlavour } from "@monorepo/shared/analytics";

export type DailyRow = {
  date: string;
  appId: string;
  dau: number;
  mau: number;
  messagesSent: number;
  picturesSent: number;
  notesCreated: number;
  b2BSeniorsCreated: number;
};

export type KpiKey = keyof Omit<DailyRow, "date" | "appId">;

export type DistributionRow = {
  date: string;
  appId: string;
  dimension: string;
  label: string;
  count: number;
};

/** Row from `analytics.content_usage_daily` (GraphQL `ContentUsageDaily`). */
export type ContentUsageDailyRow = {
  date: string;
  appId: string;
  contentId: string;
  cmsContentKind: string;
  contentSubtype: string | null | undefined;
  title: string | null | undefined;
  completedSessionCount: number;
  unfinishedSessionCount: number;
  completedDurationSecondsSum: number | string;
  completedDurationSessionCount: number;
};

export const parseDurationSum = (v: ContentUsageDailyRow["completedDurationSecondsSum"]): number => {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") return Number(v) || 0;
  return 0;
};

export const formatAvgDuration = (sumSeconds: number, sessionCount: number): string => {
  if (sessionCount <= 0) return "—";
  const avg = sumSeconds / sessionCount;
  const m = Math.floor(avg / 60);
  const s = Math.round(avg % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

export type GameSubtypeUsageRow = {
  subtype: string | null;
  completed: number;
  unfinished: number;
  durSum: number;
  durCount: number;
  avgDurationLabel: string;
};

export const aggregateGameSubtypes = (rows: ContentUsageDailyRow[]): GameSubtypeUsageRow[] => {
  const bySubtype = new Map<string | null, Omit<GameSubtypeUsageRow, "avgDurationLabel">>();
  for (const row of rows) {
    if (row.cmsContentKind !== "game") continue;
    const subtype = row.contentSubtype?.trim() || null;
    const current = bySubtype.get(subtype) ?? { subtype, completed: 0, unfinished: 0, durSum: 0, durCount: 0 };
    current.completed += row.completedSessionCount;
    current.unfinished += row.unfinishedSessionCount;
    current.durSum += parseDurationSum(row.completedDurationSecondsSum);
    current.durCount += row.completedDurationSessionCount;
    bySubtype.set(subtype, current);
  }
  return Array.from(bySubtype.values())
    .map(row => ({ ...row, avgDurationLabel: formatAvgDuration(row.durSum, row.durCount) }))
    .sort((a, b) => b.completed - a.completed || (a.subtype ?? "").localeCompare(b.subtype ?? ""));
};

export enum AnalyticsGroupBy {
  AppId = "appId",
  Flavour = "flavour",
}

export enum Granularity {
  Day = "day",
  Month = "month",
  Year = "year",
}

export enum Interval {
  D7 = "7D",
  D30 = "30D",
  M3 = "3M",
  M6 = "6M",
  M12 = "12M",
  Custom = "custom",
}

export const INTERVAL_CONFIG: Record<
  Exclude<Interval, Interval.Custom>,
  { days?: number; months?: number; granularity: Granularity }
> = {
  [Interval.D7]: { days: 7, granularity: Granularity.Day },
  [Interval.D30]: { days: 30, granularity: Granularity.Day },
  [Interval.M3]: { months: 3, granularity: Granularity.Day },
  [Interval.M6]: { months: 6, granularity: Granularity.Month },
  [Interval.M12]: { months: 12, granularity: Granularity.Month },
};

export const INTERVAL_OPTIONS = [
  Interval.D7,
  Interval.D30,
  Interval.M3,
  Interval.M6,
  Interval.M12,
  Interval.Custom,
] as const;

export const CHART_PALETTE = [color.primary, "#16A34A", "#E87722", "#8B5CF6", color.secondary, "#DC2626"];

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const truncDate = (date: string, g: Granularity): string => {
  if (g === Granularity.Month) return date.slice(0, 7);
  if (g === Granularity.Year) return date.slice(0, 4);
  return date.slice(0, 10);
};

export const generateKeys = (from: string, to: string, g: Granularity): string[] => {
  const keys: string[] = [];
  const d = new Date(from);
  const end = truncDate(to, g);
  while (truncDate(d.toISOString(), g) <= end) {
    keys.push(truncDate(d.toISOString(), g));
    if (g === Granularity.Year) d.setFullYear(d.getFullYear() + 1);
    else if (g === Granularity.Month) d.setMonth(d.getMonth() + 1);
    else d.setDate(d.getDate() + 1);
  }
  return keys;
};

export const isoToday = () => new Date().toISOString().slice(0, 10);

export const isoYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

export const computeFromDate = (interval: Exclude<Interval, Interval.Custom>): string => {
  const d = new Date();
  const cfg = INTERVAL_CONFIG[interval];
  if (cfg.days) d.setDate(d.getDate() - cfg.days);
  if (cfg.months) d.setMonth(d.getMonth() - cfg.months);
  return d.toISOString().slice(0, 10);
};

export const formatDateLabel = (key: string, g: Granularity): string => {
  if (g === Granularity.Year) return key;
  if (g === Granularity.Month) {
    const [, m] = key.split("-");
    return SHORT_MONTHS[Number(m) - 1];
  }
  const [, m, d] = key.split("-");
  return `${SHORT_MONTHS[Number(m) - 1]} ${Number(d)}`;
};

export const formatFlavour = (f: string) => f.replace(/^senior-/, "");

export const getAnalyticsGroupKey = (appId: string, groupBy: AnalyticsGroupBy): string =>
  groupBy === AnalyticsGroupBy.AppId ? appId : deriveFlavourFromAppId(appId);

export const formatAnalyticsGroupKey = (groupKey: string, groupBy: AnalyticsGroupBy): string =>
  groupBy === AnalyticsGroupBy.AppId ? groupKey : formatFlavour(groupKey);
